/** 長押し判定までの待ち（値・マルチ・丸め共通） */
export const HELD_REPEAT_THRESHOLD_MS = 300;

/** 離したあと temp 表示を保持する時間 */
export const HELD_TEMP_CLEAR_MS = 1500;

/** 押下位置からこれ以上動いたらドラッグキャンセル */
export const HELD_DRAG_CANCEL_PX = 5;

export interface RepeatTimerRefs {
  longPressTimerRef: { current: number | null };
  repeatTimerRef: { current: number | null };
}

export interface HeldRepeatTickResult {
  continue: boolean;
  intervalMs: number;
}

/** 増分値に応じた繰り返し間隔（PouchComponent 値・マルチ・丸めで共通） */
export function getRepeatInterval(increment: number): number {
  if (increment === 0.01) return 160;
  if (increment === 0.1) return 160;
  if (increment === 1) return 160;
  if (increment === 10) return 200;
  if (increment === 100) return 250;
  if (increment === 1000) return 250;
  if (increment === 10000) return 250;
  return 160;
}

export function clearRepeatTimers(refs: RepeatTimerRefs): void {
  if (refs.longPressTimerRef.current !== null) {
    window.clearTimeout(refs.longPressTimerRef.current);
    refs.longPressTimerRef.current = null;
  }
  if (refs.repeatTimerRef.current !== null) {
    window.clearTimeout(refs.repeatTimerRef.current);
    refs.repeatTimerRef.current = null;
  }
}

export function isDragBeyondThreshold(
  mouseDown: { x: number; y: number } | null,
  current?: { x: number; y: number },
  thresholdPx = HELD_DRAG_CANCEL_PX,
): boolean {
  if (!mouseDown || !current) return false;
  const dx = current.x - mouseDown.x;
  const dy = current.y - mouseDown.y;
  return Math.sqrt(dx * dx + dy * dy) > thresholdPx;
}

export function cancelTempResetTimer(tempResetTimerRef: { current: number | null }): void {
  if (tempResetTimerRef.current !== null) {
    window.clearTimeout(tempResetTimerRef.current);
    tempResetTimerRef.current = null;
  }
}

export function scheduleTempReset(
  tempResetTimerRef: { current: number | null },
  onClear: () => void,
  delayMs = HELD_TEMP_CLEAR_MS,
): void {
  cancelTempResetTimer(tempResetTimerRef);
  tempResetTimerRef.current = window.setTimeout(() => {
    onClear();
    tempResetTimerRef.current = null;
  }, delayMs);
}

/**
 * 短押し後の長押しリピートを arm する。
 * onThreshold: 300ms 経過時（長押し UI 等）
 * onTick: 各リピート。continue=false で停止。
 */
export function armHeldRepeat(
  refs: RepeatTimerRefs,
  options: {
    thresholdMs?: number;
    shouldAbort?: () => boolean;
    onThreshold: () => void;
    onTick: () => HeldRepeatTickResult;
  },
): void {
  clearRepeatTimers(refs);
  refs.longPressTimerRef.current = window.setTimeout(() => {
    if (options.shouldAbort?.()) {
      clearRepeatTimers(refs);
      return;
    }
    options.onThreshold();

    const scheduleNext = () => {
      const result = options.onTick();
      if (!result.continue) {
        refs.repeatTimerRef.current = null;
        return;
      }
      refs.repeatTimerRef.current = window.setTimeout(() => {
        if (refs.repeatTimerRef.current !== null) {
          scheduleNext();
        }
      }, result.intervalMs);
    };

    scheduleNext();
  }, options.thresholdMs ?? HELD_REPEAT_THRESHOLD_MS);
}
